"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Flame, ShoppingBag, Zap } from "lucide-react";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
const wp_query_params = {
  per_page: 8,
  product_tag: [{
    id: 45,
    name: "Trending"
  }],
  orderby: "popularity"
};
const wp_query = new WP_Query(wp_query_params);
function ProductsGridConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div data-section-id="73640" className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {[...Array(8)].map((_, i) => <div key={i} data-index={i} className="flex h-[400px] flex-col rounded-xl border border-border bg-card p-4 animate-pulse">
            <div className="aspect-square w-full rounded-lg bg-muted/50 mb-4" />
            <div className="h-4 w-3/4 rounded bg-muted/50 mb-2" />
            <div className="h-4 w-1/4 rounded bg-muted/50 mt-auto mb-4" />
            <div className="h-10 w-full rounded bg-muted/50" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex min-h-[400px] items-center justify-center rounded-xl border border-dashed border-border bg-muted/10 p-8 text-center">
        <div className="flex flex-col items-center gap-2 text-muted-foreground">
          <Zap className="h-8 w-8 opacity-50" />
          <p className="font-mono text-sm">Failed to load curated deals.</p>
        </div>
      </div>;
  }
  if (products.length === 0) {
    return <div className="flex min-h-[400px] items-center justify-center rounded-xl border border-dashed border-border bg-muted/10 p-8 text-center">
        <div className="flex flex-col items-center gap-2 text-muted-foreground">
          <ShoppingBag className="h-8 w-8 opacity-50" />
          <p className="font-mono text-sm">No exclusive tech deals available right now.</p>
        </div>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {products.map((product, i) => <article key={product.id} data-index={i} className="group relative flex flex-col overflow-hidden rounded-xl border border-border bg-card text-card-foreground transition-all duration-300 hover:border-primary/50 hover:shadow-lg">
          {}
          <Link to={product.link} className="relative block aspect-square overflow-hidden bg-muted/30">
            <Badge variant="default" className="absolute left-3 top-3 z-20 bg-secondary text-secondary-foreground font-mono text-[10px] uppercase tracking-wider shadow-lg">
              <Flame className="mr-1 h-3 w-3" />
              Limited Time Offer
            </Badge>
            
            {product.featured_image_url ? <>
                {}
                <img src={product.featured_image_url} alt={product.title} className="absolute inset-0 h-full w-full object-cover transition-opacity duration-500 group-hover:opacity-0" loading="lazy" />
                {}
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1630779/img/tech_alternate_angle_placeholder.png" alt={`${product.title} alternate view`} className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-500 group-hover:opacity-100" loading="lazy" />
              </> : <div className="flex h-full w-full items-center justify-center bg-muted/50">
                <Zap className="h-12 w-12 text-muted-foreground/30" />
              </div>}
          </Link>

          {}
          <div className="flex flex-1 flex-col p-5">
            <Link to={product.link} className="group-hover:text-primary transition-colors">
              <h3 className="font-default text-sm font-medium leading-tight line-clamp-2 mb-2">
                {product.title}
              </h3>
            </Link>
            
            <div className="mt-auto pt-4 flex items-end justify-between">
              <div className="font-mono text-lg font-bold text-primary">
                ${product.price?.toFixed(2) || "0.00"}
              </div>
              {product.discounted_price && <div className="font-mono text-xs text-muted-foreground line-through mb-1">
                  ${product.discounted_price.toFixed(2)}
                </div>}
            </div>
          </div>

          {}
          <div className="p-5 pt-0">
            <AddToCartButton productId={product.id} variant="default" className="w-full" buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs uppercase tracking-widest h-10 rounded-md transition-all" text="Add to Cart" showQuantity={false} />
          </div>
        </article>)}
    </div>;
}
export default function ViralTechDrops() {
  return <section id="trending-tech-deals" className="bg-background text-foreground py-24 px-4 md:px-8 lg:px-12 overflow-hidden relative">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="mx-auto max-w-7xl relative z-10">
        <div className="mb-12 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <Badge variant="outline" className="mb-4 font-mono text-xs border-primary/30 text-primary">
              <Zap className="mr-1 h-3 w-3" />
              Active Now
            </Badge>
            <h2 className="font-default text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
              Trending Tech Deals
            </h2>
            <p className="mt-4 font-default text-sm text-muted-foreground md:text-base max-w-xl">
              Discover the latest high-performance essentials curated for the modern digital lifestyle. 
              Secure ApexRig Tech exclusives before the timer runs out.
            </p>
          </div>
          
          <Link to="#" className="group inline-flex items-center gap-2 font-mono text-sm font-medium text-primary hover:text-primary/80 transition-colors">
            Explore Full Catalog
            <span className="inline-block transition-transform group-hover:translate-x-1">
              →
            </span>
          </Link>
        </div>

        <EcommerceProductsProvider wp_query={wp_query} className="viral-drops-grid-wrapper">
          <ProductsGridConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}